import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Lock, CheckCircle, ArrowLeft } from "lucide-react";

const ResetPassword = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const { t } = useTranslation();

  const token = searchParams.get("token");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    watch,
  } = useForm();

  const password = watch("password");

  const onSubmit = async (data) => {
    if (data.password !== data.confirmPassword) {
      setError("confirmPassword", {
        message: t(
          "auth.resetPassword.passwordMismatch",
          "Passwords do not match",
        ),
      });
      return;
    }

    if (!token) {
      setError("root", {
        message: t(
          "auth.resetPassword.invalidToken",
          "Invalid or missing reset token",
        ),
      });
      return;
    }

    const result = await resetPassword({
      token,
      password: data.password,
    });

    if (result.success) {
      setResetSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } else {
      setError("root", { message: result.error });
    }
  };

  const renderContent = () => {
    if (resetSuccess) {
      return (
        <div className="space-y-8">
          <div className="space-y-3 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
              <CheckCircle className="h-8 w-8" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                {t("auth.resetPassword.success")}
              </p>
              <h2 className="mt-2 text-3xl font-semibold text-white">
                {t("auth.resetPassword.success")}
              </h2>
              <p className="mt-2 text-sm text-slate-400">
                Your password has been successfully reset. You will be
                redirected to the login page in a few seconds.
              </p>
            </div>
          </div>
          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 text-sm font-medium text-cyan-200 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("auth.resetPassword.backToLogin")}
            </Link>
          </div>
        </div>
      );
    }

    if (!token) {
      return (
        <div className="space-y-8">
          <div className="space-y-3 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15 text-red-300">
              <Lock className="h-8 w-8" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                {t("auth.resetPassword.invalidLink")}
              </p>
              <h2 className="mt-2 text-3xl font-semibold text-white">
                {t("auth.resetPassword.invalidLink")}
              </h2>
              <p className="mt-2 text-sm text-slate-400">
                {t("auth.resetPassword.invalidLinkDesc")}
              </p>
            </div>
          </div>
          <div className="text-center">
            <Link
              to="/forgot-password"
              className="inline-flex items-center justify-center gap-2 text-sm font-medium text-cyan-200 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("auth.resetPassword.requestNewLink")}
            </Link>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="space-y-2 text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
            {t("auth.resetPassword.title")}
          </p>
          <h2 className="text-3xl font-semibold text-white">
            {t("auth.resetPassword.title")}
          </h2>
          <p className="text-sm text-slate-400">
            {t("auth.resetPassword.subtitle")}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-200">
                {t("auth.resetPassword.password")}
              </label>
              <div className="relative">
                <input
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 12,
                      message: "Password must be at least 12 characters",
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                      message:
                        "Password must contain at least one uppercase letter, one lowercase letter, and one number",
                    },
                  })}
                  type={showPassword ? "text" : "password"}
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-12 py-3 text-white placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  placeholder="Enter your new password"
                />
                <Lock className="input-icon-left text-slate-400" />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-400">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-200">
                {t("auth.resetPassword.confirmPassword")}
              </label>
              <div className="relative">
                <input
                  {...register("confirmPassword", {
                    required: "Please confirm your password",
                  })}
                  type={showConfirmPassword ? "text" : "password"}
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/40 px-12 py-3 text-white placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  placeholder="Confirm your new password"
                />
                <Lock className="input-icon-left text-slate-400" />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-400">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          {errors.root && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
              {errors.root.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-cyan-500/30 transition hover:shadow-cyan-400/50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              t("auth.resetPassword.submit", "Reset password")
            )}
          </button>

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 text-sm font-medium text-cyan-200 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("auth.resetPassword.backToLogin")}
            </Link>
          </div>
        </form>
      </>
    );
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#020617] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute -top-32 right-[-10%] h-96 w-96 rounded-full bg-cyan-500/30 blur-[120px]" />
        <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-blue-500/20 blur-[120px]" />
        <div className="absolute inset-0 opacity-20">
          <div className="[background:radial-gradient(circle_at_1px,_rgba(255,255,255,0.25)_1px,_transparent_0)] h-full w-full [background-size:40px_40px]" />
        </div>
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-slate-900/70 p-8 shadow-2xl shadow-cyan-500/20 backdrop-blur-xl">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
